import { AnnotatedGraph, AnnotatedSuperNodeT } from '@litlingo/react';
import { setInlineCommentMode, setInlineCommentReference } from 'actions';
import { COMMENT_ICON } from 'constants/envelopeIcons';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getInlineCommentReference } from 'selectors/envelopeReview';
import { getSingleEnvelopeComments } from 'selectors/envelopes';
import { useSelector } from 'store';
import { EnvelopeAction } from 'types';
import { HoverData } from './WrapperHighlightedSentence';

type CommentButtonComponentProps = {
  index: number;
  line: AnnotatedGraph;
  communicationId: string;
  hoverData: HoverData;
  group: AnnotatedSuperNodeT[];
  containerRef: React.RefObject<HTMLDivElement>;
};

const CommentButton: React.FC<CommentButtonComponentProps> = ({
  index,
  line,
  communicationId,
  hoverData,
  group,
  containerRef,
}) => {
  const dispatch = useDispatch();

  const { envelopeId } = useParams<{ envelopeId: string }>();

  const comments = useSelector((state) => getSingleEnvelopeComments(state, envelopeId));
  const commentReference = useSelector(getInlineCommentReference);

  const [node, setNode] = useState<HTMLDivElement | null>(null);

  const isSelected =
    commentReference.lineNumber === index &&
    commentReference.nodes[0] === group[0].index &&
    commentReference.nodes[1] === group[group.length - 1].index &&
    commentReference.communicationId === communicationId;

  useEffect(() => {
    let id: NodeJS.Timeout | null = null;
    if (!node) {
      id = setInterval(() => {
        if (containerRef.current) {
          const superNode = containerRef.current.querySelector(`#supernode-${group[0].index}`);
          if (superNode) {
            setNode(superNode as HTMLDivElement);
          }
        }
      }, 100);
    }

    return () => {
      if (id) clearInterval(id);
    };
  }, [containerRef, group, node]);

  useEffect(() => {
    if (node) {
      if (isSelected) {
        node?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected]);

  let yPos = 0;
  if (node && containerRef?.current) {
    yPos = node.getBoundingClientRect().y - containerRef.current.getBoundingClientRect().y + 4;
  }

  const renderCommentButton = (): JSX.Element => {
    if (group.length === 0) {
      return <></>;
    }

    const commentsFilter = (c: EnvelopeAction): boolean =>
      // @ts-ignore
      c.payload.lineIndex === index &&
      // @ts-ignore
      c.payload.nodes?.[0] === group[0].index &&
      // @ts-ignore
      c.payload.communicationId === communicationId;

    const groupComments = comments.filter(commentsFilter);

    const hoverInGroup = group.some((g) => g.index === hoverData.hoverIndex);

    // when hovering over group
    if (hoverInGroup && hoverData.line === line.index) {
      return (
        <button
          type="button"
          className="absolute w-4 text-small focus:outline-none"
          style={{ right: 0, top: yPos }}
          onClick={(): void => {
            dispatch(setInlineCommentMode(true));
            dispatch(
              setInlineCommentReference({
                lineNumber: index,
                nodes: [hoverData.firstNode, hoverData.lastNode],
                text: '',
                communicationId,
              })
            );
          }}
        >
          <span className={`${isSelected ? 'text-litlingo-gray-6' : 'text-litlingo-gray-4'}`}>
            {COMMENT_ICON()}
          </span>
          {groupComments.length > 0 && (
            <span
              className={`absolute h-4 w-4 flex items-center justify-center ${
                isSelected ? 'bg-litlingo-primary-60' : 'bg-litlingo-gray-1'
              } rounded-full`}
              style={{ top: -8, right: -8 }}
            >
              {groupComments.length}
            </span>
          )}
        </button>
      );
    }

    // no hover, but group has comments
    if (groupComments.length > 0) {
      return (
        <button
          type="button"
          className="absolute w-4 text-small focus:outline-none"
          style={{ right: 0, top: yPos }}
          onClick={(): void => {
            dispatch(setInlineCommentMode(true));
            dispatch(
              setInlineCommentReference({
                lineNumber: index,
                nodes: [group[0].index, group[group.length - 1].index],
                text: '',
                communicationId,
              })
            );
          }}
        >
          <span className={`${isSelected ? 'text-litlingo-gray-6' : 'text-litlingo-gray-4'}`}>
            {COMMENT_ICON()}
          </span>
          {groupComments.length > 0 && (
            <span
              className={`absolute h-4 w-4 flex items-center justify-center ${
                isSelected ? 'bg-litlingo-primary-60' : 'bg-litlingo-gray-1'
              } rounded-full`}
              style={{ top: -8, right: -8 }}
            >
              {groupComments.length}
            </span>
          )}
        </button>
      );
    }

    return <></>;
  };

  return renderCommentButton();
};

export default CommentButton;
